import { useState, useRef, useCallback, useEffect } from 'react';
import { useGlobalPreview } from '../hooks/useGlobalPreview';
import GlobalPreview from './GlobalPreview';

const SIDEBAR_WIDTH = 240; // matches --sidebar-width
const HANDLE_WIDTH = 5;
const MIN_PREVIEW_FRAC = 0.2;
const MAX_PREVIEW_FRAC = 0.72;

/**
 * Inner layout body that can access the GlobalPreview context.
 * Adds 'has-preview' class when the global preview panel is open.
 * Renders a drag handle between editor and preview to resize the split.
 */
export default function LayoutBody({
  children,
  siteId,
  sidebar,
}: {
  children: React.ReactNode;
  siteId: string | undefined;
  sidebar: React.ReactNode;
}) {
  const { isOpen } = useGlobalPreview();
  const [previewFrac, setPreviewFrac] = useState(0.5);
  const [isDragging, setIsDragging] = useState(false);
  const bodyRef = useRef<HTMLDivElement>(null);
  const dragging = useRef(false);

  const onHandleMouseDown = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    dragging.current = true;
    setIsDragging(true);
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
  }, []);

  useEffect(() => {
    if (!isOpen) return;

    const onMouseMove = (e: MouseEvent) => {
      if (!dragging.current || !bodyRef.current) return;
      const rect = bodyRef.current.getBoundingClientRect();
      const available = rect.width - SIDEBAR_WIDTH - HANDLE_WIDTH;
      const fromRight = rect.right - e.clientX;
      const frac = Math.min(MAX_PREVIEW_FRAC, Math.max(MIN_PREVIEW_FRAC, fromRight / available));
      setPreviewFrac(frac);
    };

    const onMouseUp = () => {
      if (dragging.current) {
        dragging.current = false;
        setIsDragging(false);
        document.body.style.cursor = '';
        document.body.style.userSelect = '';
      }
    };

    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp);
    return () => {
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
    };
  }, [isOpen]);

  return (
    <div
      className={`layout-body${isOpen ? ' has-preview' : ''}`}
      ref={bodyRef}
      style={isOpen ? ({ '--preview-frac': previewFrac } as React.CSSProperties) : undefined}
    >
      {/* Transparent overlay during drag to prevent iframe from stealing mouse events */}
      {isDragging && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 9999, cursor: 'col-resize' }} />
      )}
      {sidebar}
      <main className={`main-content ${siteId ? 'with-sidebar' : ''}`}>
        {children}
      </main>
      {siteId && isOpen && (
        <>
          <div
            className={`preview-resize-handle${isDragging ? ' preview-resize-handle--dragging' : ''}`}
            onMouseDown={onHandleMouseDown}
          />
          <GlobalPreview />
        </>
      )}
    </div>
  );
}
